// build: 2026-02-pmax-phase2-v3 (cache-bust)
import { useState, useEffect, useCallback } from 'react';
import {
  Cpu, Play, RefreshCw, Loader2, CheckCircle,
  ChevronDown, ChevronRight, AlertTriangle, TrendingUp, Target,
  Zap, Shield, Brain, ArrowLeft, ThumbsUp, ThumbsDown, Minus, Link2, Bot, Sparkles, Rocket, Plus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { authFetch, isPostMessageNoise } from '../lib/api';
import OptimizationAutomation from './OptimizationAutomation';
import OptimizationSkai from './OptimizationSkai';
import AccountPicker from '../components/AccountPicker';

const API = process.env.REACT_APP_BACKEND_URL;

const AGENT_ICONS = {
  strategy_architecture: Target,
  keyword_intelligence: Zap,
  bidding_budget: TrendingUp,
  campaign_hygiene: Shield,
  pmax_optimization: Rocket,
  synthesis: Brain,
};

// Agents that are actually implemented on the backend (have runners).
// Other phase-2 entries in the registry are placeholders.
const IMPLEMENTED_AGENTS = new Set([
  'strategy_architecture', 'keyword_intelligence', 'bidding_budget', 'campaign_hygiene',
  'pmax_optimization',
]);

const PRIORITY_STYLES = {
  critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', badge: 'bg-red-100 text-red-700' },
  high: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', badge: 'bg-amber-100 text-amber-700' },
  medium: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', badge: 'bg-blue-100 text-blue-700' },
  low: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600', badge: 'bg-gray-100 text-gray-600' },
};

const STATUS_STYLES = {
  pending: 'bg-gray-100 text-gray-600',
  accepted: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-600',
  deferred: 'bg-amber-100 text-amber-700',
  implemented: 'bg-blue-100 text-blue-700',
};

// ═══════════════ RECOMMENDATION CARD ═══════════════
const RecommendationCard = ({ rec, onAction }) => {
  const ps = PRIORITY_STYLES[rec.priority] || PRIORITY_STYLES.medium;
  const ss = STATUS_STYLES[rec.status] || STATUS_STYLES.pending;

  return (
    <div className={`${ps.bg} border ${ps.border} rounded-lg p-4`} data-testid={`rec-${rec.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${ps.badge}`}>{rec.priority}</span>
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${ss}`}>{rec.status}</span>
            {rec.confidence && (
              <span className="text-[10px] text-text-tertiary">{(rec.confidence * 100).toFixed(0)}% confidence</span>
            )}
          </div>
          <p className={`text-sm font-medium ${ps.text}`}>{rec.action}</p>
          <p className="text-xs text-text-secondary mt-1">{rec.rationale}</p>
          {rec.expected_impact && (
            <p className="text-xs text-text-tertiary mt-1">Expected: {rec.expected_impact}</p>
          )}
        </div>
        {rec.status === 'pending' && (
          <div className="flex gap-1 flex-shrink-0">
            <button onClick={() => onAction(rec.id, 'accepted')} className="p-1.5 rounded hover:bg-emerald-100" title="Accept" data-testid={`accept-${rec.id}`}>
              <ThumbsUp size={14} className="text-emerald-600" />
            </button>
            <button onClick={() => onAction(rec.id, 'deferred')} className="p-1.5 rounded hover:bg-amber-100" title="Defer" data-testid={`defer-${rec.id}`}>
              <Minus size={14} className="text-amber-600" />
            </button>
            <button onClick={() => onAction(rec.id, 'rejected')} className="p-1.5 rounded hover:bg-red-100" title="Reject" data-testid={`reject-${rec.id}`}>
              <ThumbsDown size={14} className="text-red-500" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════ AGENT RUN CARD ═══════════════
const AgentRunCard = ({ run, onViewDetails }) => {
  const Icon = AGENT_ICONS[run.agent_type] || Cpu;
  const isComplete = run.status === 'complete';
  const isFailed = run.status === 'failed';

  return (
    <Card className="cursor-pointer hover:shadow-sm transition-shadow" onClick={() => onViewDetails(run)} data-testid={`run-${run.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isComplete ? 'bg-emerald-50 text-emerald-600' : isFailed ? 'bg-red-50 text-red-500' : 'bg-brand/10 text-brand'}`}>
            {run.status === 'running' ? <Loader2 size={18} className="animate-spin" /> : <Icon size={18} />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-text-primary">{run.agent_name}</p>
            <div className="flex items-center gap-2 text-xs text-text-tertiary mt-0.5">
              {isComplete && (
                <>
                  <span>{run.findings?.length || 0} findings</span>
                  <span>|</span>
                  <span>{run.recommendations?.length || 0} recs</span>
                </>
              )}
              {isFailed && <span className="text-red-500">{run.error || 'Failed'}</span>}
              {run.duration_seconds && <span>| {run.duration_seconds}s</span>}
            </div>
          </div>
          <ChevronRight size={16} className="text-text-muted" />
        </div>
      </CardContent>
    </Card>
  );
};

// ═══════════════ RUN DETAIL VIEW ═══════════════
const RunDetail = ({ run, onBack, onRecAction }) => {
  const Icon = AGENT_ICONS[run.agent_type] || Cpu;
  const [showFindings, setShowFindings] = useState(true);

  return (
    <div className="space-y-5" data-testid="run-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="run-back-btn">
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>
        <Icon size={18} className="text-brand" />
        <div>
          <h3 className="text-lg font-bold text-text-primary">{run.agent_name}</h3>
          <p className="text-xs text-text-tertiary">
            {new Date(run.started_at).toLocaleString()} | {run.duration_seconds}s | {run.trigger}
          </p>
        </div>
      </div>

      {run.summary && (
        <div className="bg-surface-page rounded-lg p-4">
          <p className="text-sm text-text-secondary">{run.summary}</p>
        </div>
      )}

      {/* Findings */}
      <div>
        <button onClick={() => setShowFindings(!showFindings)} className="flex items-center gap-2 mb-3">
          {showFindings ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="text-sm font-semibold uppercase tracking-wider text-text-muted">
            Findings ({run.findings?.length || 0})
          </span>
        </button>
        {showFindings && (
          <div className="space-y-2">
            {(run.findings || []).map(f => (
              <div key={f.id} className="border border-border-subtle rounded-lg p-3" data-testid={`finding-${f.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${PRIORITY_STYLES[f.impact]?.badge || 'bg-gray-100'}`}>
                    {f.impact}
                  </span>
                  <span className="text-xs text-text-tertiary">{f.category}</span>
                </div>
                <p className="text-sm font-medium text-text-primary">{f.title}</p>
                <p className="text-xs text-text-secondary mt-1">{f.description}</p>
                {f.evidence && <p className="text-[11px] text-text-tertiary mt-1 italic">Evidence: {f.evidence}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recommendations */}
      {(run.recommendations || []).length > 0 && (
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-text-muted mb-3">
            Recommendations ({run.recommendations.length})
          </p>
          <div className="space-y-2">
            {run.recommendations.map(rec => (
              <RecommendationCard key={rec.id} rec={rec} onAction={onRecAction} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════ MAIN OPTIMIZATION CENTER ═══════════════
const OptimizationCenter = ({ user }) => {
  const [view, setView] = useState('dashboard'); // dashboard | detail
  const [activeTab, setActiveTab] = useState('analysis'); // analysis | automation
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [agents, setAgents] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [latestSynthesis, setLatestSynthesis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [running, setRunning] = useState(false);
  const [dataSummary, setDataSummary] = useState(null);
  const [connectingAds, setConnectingAds] = useState(false);
  const [adsAuthStatus, setAdsAuthStatus] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [acctRes, agentRes, authRes] = await Promise.all([
        authFetch(`${API}/api/optimization/accounts`),
        authFetch(`${API}/api/optimization/agents`),
        authFetch(`${API}/api/google-ads/auth/status`),
      ]);
      if (acctRes.ok) {
        const d = await acctRes.json();
        setAccounts(d.accounts || []);
        if (d.accounts?.length > 0 && !selectedAccount) {
          setSelectedAccount(d.accounts[0]);
        }
      }
      if (agentRes.ok) {
        const d = await agentRes.json();
        setAgents(d.agents || []);
      }
      if (authRes.ok) {
        setAdsAuthStatus(await authRes.json());
      }
    } catch (e) { console.error('Load error:', e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAccountData = useCallback(async (accountId) => {
    if (!accountId) return;
    try {
      const [runsRes, synthRes, dataRes] = await Promise.all([
        authFetch(`${API}/api/optimization/accounts/${accountId}/runs`),
        authFetch(`${API}/api/optimization/accounts/${accountId}/latest`),
        authFetch(`${API}/api/optimization/accounts/${accountId}/data`),
      ]);
      if (runsRes.ok) setRuns((await runsRes.json()).runs || []);
      if (synthRes.ok) {
        const s = await synthRes.json();
        if (s.id) setLatestSynthesis(s);
      }
      if (dataRes.ok) setDataSummary(await dataRes.json());
    } catch (e) { console.error('Account data error:', e); }
     
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (selectedAccount) loadAccountData(selectedAccount.id); }, [selectedAccount, loadAccountData]);

  // Detect OAuth return — show success toast, clean the URL, trigger data reload.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('ads_connected') === '1') {
      toast.success('Google Ads connected successfully.');
      window.history.replaceState({}, '', window.location.pathname);
      loadData();
    } else if (params.get('ads_error')) {
      toast.error(`Google Ads authorization failed: ${params.get('ads_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectGoogleAds = async () => {
    // Open the popup SYNCHRONOUSLY in the click handler — browsers block
    // popups opened after `await`. We open about:blank now and navigate it
    // once we have Google's URL. If the popup is still blocked, the user
    // explicitly allowed-clicked, which most browsers honor.
    const win = window.open('about:blank', 'google_ads_oauth', 'width=520,height=720');
    if (!win) {
      toast.error('Popup blocked — please allow popups for vos.beebyclarkmeyler.com and try again.');
      return;
    }
    try {
      // Use safe DOM manipulation instead of document.write (which is XSS-prone
      // and deprecated in modern browsers). The content is a fixed string
      // controlled by us, but switching to textContent / element appends keeps
      // future-proofing simple.
      const doc = win.document;
      doc.title = "Preparing Google sign-in…";
      const body = doc.body || doc.documentElement.appendChild(doc.createElement("body"));
      body.style.cssText =
        "font-family:-apple-system,sans-serif;display:flex;align-items:center;" +
        "justify-content:center;height:100vh;color:#555;margin:0;background:#fafafa";
      const msg = doc.createElement("div");
      msg.textContent = "Preparing Google sign-in…";
      body.replaceChildren(msg);
    } catch (popupErr) {
      // Cross-origin or about:blank quirks — non-fatal; loader is cosmetic
      // eslint-disable-next-line no-console
      console.debug('OAuth popup pre-render skipped:', popupErr?.message);
    }

    setConnectingAds(true);
    try {
      const res = await authFetch(`${API}/api/google-ads/auth/start`);
      if (!res.ok) throw new Error('Failed to start Google Ads authorization');
      const data = await res.json();
      win.location.href = data.authorization_url;
      toast.info('Authorize Google Ads in the popup window…');

      const poll = setInterval(() => {
        if (win.closed) {
          clearInterval(poll);
          setConnectingAds(false);
          toast.success('Refreshing Google Ads connection…');
          loadData();
        }
      }, 800);
    } catch (e) {
      try {
        win.close();
      } catch (closeErr) {
        // Popup may have closed itself; non-fatal
        // eslint-disable-next-line no-console
        console.debug('Popup close skipped:', closeErr?.message);
      }
      if (!isPostMessageNoise(e)) {
        toast.error(e.message);
      }
      setConnectingAds(false);
    }
  };

  const addManualAccount = async () => {
    const customerId = window.prompt('Enter Google Ads Customer ID (e.g., 123-456-7890):');
    if (!customerId) return;
    const name = window.prompt('Account name:', 'Google Ads Account') || 'Google Ads Account';

    try {
      // Get current refresh token from auth status
      const statusRes = await authFetch(`${API}/api/google-ads/auth/status`);
      const status = statusRes.ok ? await statusRes.json() : {};
      if (!status.has_refresh_token) {
        toast.error('Connect Google Ads OAuth first');
        return;
      }

      const res = await authFetch(`${API}/api/optimization/accounts/connect-manual`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId.replace(/-/g, ''), name }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to add account');
      }
      toast.success('Account added');
      loadData();
    } catch (e) { toast.error(e.message); }
  };

  const tryDemoMode = async () => {
    try {
      const res = await authFetch(`${API}/api/optimization/accounts/demo`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create demo account');
      }
      toast.success('Demo account ready — synthetic data loaded');
      loadData();
    } catch (e) { toast.error(e.message); }
  };

  const syncData = async () => {
    if (!selectedAccount) return;
    setSyncing(true);
    try {
      const res = await authFetch(`${API}/api/optimization/accounts/${selectedAccount.id}/sync`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date_range: 'LAST_30_DAYS' }),
      });
      if (!res.ok) throw new Error('Sync failed');
      const data = await res.json();
      toast.success(`Synced: ${data.stats.campaigns} campaigns, ${data.stats.keywords} keywords`);
      loadAccountData(selectedAccount.id);
    } catch (e) { toast.error(e.message); }
    finally { setSyncing(false); }
  };

  const runAllAgents = async () => {
    if (!selectedAccount) return;
    setRunning(true);
    try {
      const res = await authFetch(`${API}/api/optimization/accounts/${selectedAccount.id}/run`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Agent run failed');
      const data = await res.json();
      toast.success(`${data.agents_run.length} agents completed`);
      loadAccountData(selectedAccount.id);
    } catch (e) {
      // PostHog session-recording can throw a postMessage/DataClone error mid-fetch;
      // the actual run still completes server-side. Refresh state silently in that case.
      if (isPostMessageNoise(e)) {
        loadAccountData(selectedAccount.id);
      } else {
        toast.error(e.message);
      }
    }
    finally { setRunning(false); }
  };

  const runSpecificAgent = async (agentType) => {
    if (!selectedAccount) return;
    setRunning(true);
    try {
      const res = await authFetch(`${API}/api/optimization/accounts/${selectedAccount.id}/run`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agents: [agentType] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Agent run failed');
      }
      const data = await res.json();
      toast.success(`${agentType} completed`, { description: `${data.agents_run.length} run logged` });
      loadAccountData(selectedAccount.id);
    } catch (e) {
      if (isPostMessageNoise(e)) {
        loadAccountData(selectedAccount.id);
      } else {
        toast.error(e.message);
      }
    }
    finally { setRunning(false); }
  };

  const handleRecAction = async (recId, status) => {
    try {
      const res = await authFetch(`${API}/api/optimization/recommendations/${recId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        toast.success(`Recommendation ${status}`);
        if (selectedAccount) loadAccountData(selectedAccount.id);
        if (selectedRun) {
          // Refresh the run detail
          const runRes = await authFetch(`${API}/api/optimization/runs/${selectedRun.id}`);
          if (runRes.ok) setSelectedRun(await runRes.json());
        }
      }
    } catch (e) { toast.error('Failed to update recommendation'); }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-brand" size={24} /></div>;

  // Run Detail View
  if (view === 'detail' && selectedRun) {
    return <RunDetail run={selectedRun} onBack={() => { setView('dashboard'); setSelectedRun(null); }} onRecAction={handleRecAction} />;
  }

  // Implemented agents grouped by phase
  const phase1Agents = agents.filter(a => a.phase === 1 && a.type !== 'synthesis' && IMPLEMENTED_AGENTS.has(a.type));
  const phase2Agents = agents.filter(a => a.phase === 2 && IMPLEMENTED_AGENTS.has(a.type));

  return (
    <div className="space-y-6" data-testid="optimization-center" data-build="pmax-phase2-v3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Optimization Center</h1>
          <p className="text-sm text-text-tertiary mt-1">
            AI-powered paid search analysis with specialist agents
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedAccount?.is_demo && (
            <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-amber-100 text-amber-800 border border-amber-200" title="Synthetic dataset — Google Ads API not yet approved" data-testid="demo-mode-badge">
              Demo Mode
            </span>
          )}
          {selectedAccount && (
            <>
              <Button variant="outline" size="sm" onClick={syncData} disabled={syncing} data-testid="sync-btn">
                {syncing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />}
                {syncing ? 'Syncing...' : 'Sync Data'}
              </Button>
              <Button size="sm" onClick={runAllAgents} disabled={running || !dataSummary?.synced} className="bg-brand hover:bg-brand-hover" data-testid="run-agents-btn">
                {running ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Play size={14} className="mr-1" />}
                {running ? 'Running...' : 'Run All Agents'}
              </Button>
            </>
          )}
          {!adsAuthStatus?.has_refresh_token ? (
            <Button variant="outline" size="sm" onClick={connectGoogleAds} disabled={connectingAds} data-testid="connect-ads-header-btn">
              {connectingAds ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Link2 size={14} className="mr-1" />}
              Connect Ads
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={connectGoogleAds} disabled={connectingAds} data-testid="reconnect-ads-header-btn" title="Re-authorize Google Ads (e.g., after dev token approval)">
              {connectingAds ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Link2 size={14} className="mr-1" />}
              Reconnect Ads
            </Button>
          )}
          {adsAuthStatus?.has_refresh_token && (
            <Button variant="outline" size="sm" onClick={addManualAccount} data-testid="add-account-header-btn">
              <Plus size={14} className="mr-1" />
              Add Account
            </Button>
          )}
        </div>
      </div>

      {/* Account selector */}
      {accounts.length === 0 ? (
        <Card className="py-12 text-center" data-testid="no-accounts">
          <Link2 size={40} className="mx-auto text-text-muted mb-3" />
          <h3 className="text-lg font-semibold text-text-primary mb-1">Connect Google Ads</h3>
          <p className="text-sm text-text-tertiary mb-6 max-w-md mx-auto">
            Connect your Google Ads account to sync campaign data and run AI-powered optimization agents
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button onClick={connectGoogleAds} disabled={connectingAds} className="bg-brand hover:bg-brand-hover" data-testid="connect-ads-btn">
              {connectingAds ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Link2 size={14} className="mr-2" />}
              {connectingAds ? 'Authorizing...' : 'Connect via Google OAuth'}
            </Button>
            {adsAuthStatus?.has_refresh_token && (
              <Button variant="outline" onClick={addManualAccount} data-testid="add-manual-account-btn">
                Add Account by ID
              </Button>
            )}
            <Button variant="outline" onClick={tryDemoMode} className="border-chart-purple/40 text-chart-purple hover:bg-chart-purple/10" data-testid="try-demo-btn">
              <Sparkles size={14} className="mr-2" />
              Try Demo Mode
            </Button>
          </div>
          {adsAuthStatus?.has_refresh_token && (
            <p className="text-xs text-emerald-600 mt-3 flex items-center justify-center gap-1">
              <CheckCircle size={12} /> Google Ads OAuth connected
            </p>
          )}
          {adsAuthStatus?.discovery_error && (
            <div className="mt-4 mx-auto max-w-2xl bg-amber-50 border border-amber-200 rounded-lg p-4 text-left" data-testid="discovery-error-banner">
              <div className="flex items-start gap-3">
                <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-amber-900 mb-1">Account discovery blocked by Google</p>
                  <p className="text-amber-800 text-xs mb-2">{adsAuthStatus.discovery_error}</p>
                  {adsAuthStatus.discovery_error.includes('TEST mode') && (
                    <div className="text-xs text-amber-800 space-y-1">
                      <p className="font-semibold">How to fix:</p>
                      <ol className="list-decimal list-inside space-y-0.5">
                        <li>Open Google Ads UI and switch to your MCC ({adsAuthStatus.manager_customer_id})</li>
                        <li>Go to <b>Tools &amp; Settings</b> &rarr; <b>API Center</b></li>
                        <li>Click <b>Apply for Basic Access</b> (free, ~1–3 business days)</li>
                        <li>Once approved, click <b>Connect via Google OAuth</b> again</li>
                      </ol>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Card>
      ) : (
        <>
          {/* Account Selector + Data Status */}
          {accounts.length > 0 && (
            <Card data-testid="data-status">
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm">
                    {accounts.length > 1 ? (
                      <AccountPicker
                        accounts={accounts}
                        selected={selectedAccount}
                        onSelect={setSelectedAccount}
                      />
                    ) : (
                      <span className="font-medium text-text-primary">{selectedAccount?.name}</span>
                    )}
                    <span className="text-text-tertiary">ID: {selectedAccount?.customer_id}</span>
                    {dataSummary?.synced ? (
                      <>
                        <span className="text-emerald-600 flex items-center gap-1">
                          <CheckCircle size={12} /> Synced {new Date(dataSummary.synced_at).toLocaleDateString()}
                        </span>
                        <span className="text-text-tertiary">
                          {dataSummary.stats?.campaigns} campaigns | {dataSummary.stats?.keywords} keywords
                        </span>
                      </>
                    ) : (
                      <span className="text-amber-600 flex items-center gap-1">
                        <AlertTriangle size={12} /> No data synced yet
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tab Navigation */}
          {accounts.length > 0 && (
            <div className="flex items-center gap-1 border-b border-border-subtle" data-testid="optimization-tabs">
              <button
                onClick={() => setActiveTab('analysis')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'analysis' ? 'border-brand text-brand' : 'border-transparent text-text-tertiary hover:text-text-primary'}`}
                data-testid="tab-analysis"
              >
                <Brain size={14} className="inline mr-1.5" /> Analysis
              </button>
              <button
                onClick={() => setActiveTab('automation')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'automation' ? 'border-brand text-brand' : 'border-transparent text-text-tertiary hover:text-text-primary'}`}
                data-testid="tab-automation"
              >
                <Bot size={14} className="inline mr-1.5" /> Automation
              </button>
              <button
                onClick={() => setActiveTab('skai')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'skai' ? 'border-brand text-brand' : 'border-transparent text-text-tertiary hover:text-text-primary'}`}
                data-testid="tab-skai"
              >
                <Sparkles size={14} className="inline mr-1.5" /> Skai (Cross-Channel)
              </button>
            </div>
          )}

          {activeTab === 'automation' ? (
            <OptimizationAutomation account={selectedAccount} agents={agents} />
          ) : activeTab === 'skai' ? (
            <OptimizationSkai accounts={accounts} />
          ) : (
          <>
          {/* Synthesis Summary */}
          {latestSynthesis && (
            <Card data-testid="synthesis-summary">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Brain size={16} className="text-brand" />
                  Latest Analysis — {new Date(latestSynthesis.started_at).toLocaleDateString()}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-text-secondary mb-4">{latestSynthesis.summary}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['critical', 'high', 'medium', 'low'].map(pri => {
                    const count = (latestSynthesis.recommendations || []).filter(r => r.priority === pri).length;
                    const ps = PRIORITY_STYLES[pri];
                    return (
                      <div key={pri} className={`${ps.bg} border ${ps.border} rounded-lg p-3 text-center`}>
                        <p className="text-2xl font-bold text-text-primary">{count}</p>
                        <p className={`text-xs font-medium uppercase ${ps.text}`}>{pri}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Agent Grid */}
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider text-text-muted mb-3">Phase 1 Agents</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {phase1Agents.map(agent => {
                const Icon = AGENT_ICONS[agent.type] || Cpu;
                const lastRun = runs.find(r => r.agent_type === agent.type && r.status === 'complete');
                const clickable = !!lastRun;
                return (
                  <Card
                    key={agent.type}
                    className={`transition-shadow ${clickable ? 'hover:shadow-md cursor-pointer' : 'opacity-90'}`}
                    onClick={clickable ? () => { setSelectedRun(lastRun); setView('detail'); } : undefined}
                    data-testid={`agent-${agent.type}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-9 h-9 rounded-lg bg-brand/10 flex items-center justify-center">
                          <Icon size={16} className="text-brand" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-text-primary">{agent.name}</p>
                          <p className="text-[10px] text-text-tertiary uppercase">{agent.cadence}</p>
                        </div>
                        {clickable && <ChevronRight size={14} className="text-text-muted" />}
                      </div>
                      <p className="text-xs text-text-secondary line-clamp-2">{agent.description}</p>
                      {lastRun && (
                        <div className="mt-2 pt-2 border-t border-border-subtle flex items-center gap-2 text-xs">
                          <span className="font-semibold text-brand">{lastRun.findings?.length || 0}</span>
                          <span className="text-text-tertiary">findings</span>
                          <span className="text-text-muted">·</span>
                          <span className="font-semibold text-brand">{lastRun.recommendations?.length || 0}</span>
                          <span className="text-text-tertiary">recs</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Phase 2 Agent Grid (Specialist) */}
          {phase2Agents.length > 0 && (
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-text-muted mb-3 flex items-center gap-2">
                Phase 2 Specialists
                <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-chart-purple/10 text-chart-purple">New</span>
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {phase2Agents.map(agent => {
                  const Icon = AGENT_ICONS[agent.type] || Cpu;
                  const lastRun = runs.find(r => r.agent_type === agent.type && r.status === 'complete');
                  const clickable = !!lastRun;
                  return (
                    <Card
                      key={agent.type}
                      className={`border-chart-purple/20 transition-shadow ${clickable ? 'hover:shadow-md cursor-pointer' : 'opacity-90'}`}
                      onClick={clickable ? () => { setSelectedRun(lastRun); setView('detail'); } : undefined}
                      data-testid={`agent-${agent.type}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-9 h-9 rounded-lg bg-chart-purple/10 flex items-center justify-center flex-shrink-0">
                              <Icon size={16} className="text-chart-purple" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-text-primary">{agent.name}</p>
                              <p className="text-[10px] text-text-tertiary uppercase">{agent.cadence}</p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={running || !dataSummary?.synced}
                            onClick={(e) => { e.stopPropagation(); runSpecificAgent(agent.type); }}
                            className="text-chart-purple border-chart-purple/30 hover:bg-chart-purple/10"
                            data-testid={`run-${agent.type}`}
                          >
                            {running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                          </Button>
                        </div>
                        <p className="text-xs text-text-secondary line-clamp-2">{agent.description}</p>
                        {lastRun && (
                          <div className="mt-2 pt-2 border-t border-border-subtle flex items-center gap-2 text-xs">
                            <span className="font-semibold text-chart-purple">{lastRun.findings?.length || 0}</span>
                            <span className="text-text-tertiary">findings</span>
                            <span className="text-text-muted">·</span>
                            <span className="font-semibold text-chart-purple">{lastRun.recommendations?.length || 0}</span>
                            <span className="text-text-tertiary">recs</span>
                            <span className="ml-auto text-brand font-medium">View →</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent Runs */}
          {runs.length > 0 && (
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-text-muted mb-3">Recent Runs</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {runs.slice(0, 10).map(run => (
                  <AgentRunCard key={run.id} run={run} onViewDetails={(r) => { setSelectedRun(r); setView('detail'); }} />
                ))}
              </div>
            </div>
          )}

          {/* Top Recommendations */}
          {latestSynthesis?.recommendations?.length > 0 && (
            <div data-testid="top-recommendations">
              <p className="text-sm font-semibold uppercase tracking-wider text-text-muted mb-3">
                Top Recommendations ({latestSynthesis.recommendations.length})
              </p>
              <div className="space-y-2">
                {latestSynthesis.recommendations.slice(0, 10).map(rec => (
                  <RecommendationCard key={rec.id} rec={rec} onAction={handleRecAction} />
                ))}
              </div>
            </div>
          )}
          </>
          )}
        </>
      )}
    </div>
  );
};

export default OptimizationCenter;
